'use client';

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { PropsWithChildren, useState } from 'react';

const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

export default function ReactQueryProvider({ children }: PropsWithChildren) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
          },
        },
      }),
  );

  // 브라우저 환경에서만 localStorage 접근 (SSR 대응)
  const [persister] = useState(() => {
    if (typeof window !== 'undefined') {
      return createSyncStoragePersister({
        storage: window.localStorage,
        key: '3b-query-cache',
        throttleTime: 1000,
      });
    }

    // SSR 환경에서는 더미 persister 반환
    return {
      persistClient: () => Promise.resolve(),
      restoreClient: () => Promise.resolve(undefined),
      removeClient: () => Promise.resolve(),
    };
  });

  // 특정 쿼리만 캐싱에 포함시키는 커스텀 필터 함수
  const shouldDehydrateQuery = (query: any) => {
    return query.meta?.persist === true;
  };

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: ONE_WEEK_IN_MS,
        buster: process.env.NEXT_PUBLIC_CACHE_BUSTER, // 데이터 업데이트시 환경변수 업데이트 필요
        dehydrateOptions: {
          shouldDehydrateQuery, // meta.persist: true인 쿼리만 캐싱
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
